<template>
  <v-list-item
    :key="r.id"
    class="pa-0"
    :value="r.id"
  >
    <cancel-reservation-dialog
      v-if="confirmCancelReservation"
      :reservation="reservation"
      @refund-reservation="cancelReservation(true)"
      @cancel-reservation="cancelReservation"
      @close="confirmCancelReservation=false"
    />
    <v-list-item-content :class="['mh-48 pb-1 pl-0 pr-2', `${compact ? 'pt-05' : 'pt-1'}`]">
      <v-lazy class="w-100">
        <!-- Compact View -->
        <div
          v-if="compact"
          class="d-flex w-100"
        >
          <div
            v-if="!r.isBlock"
            class="d-flex flex-column align-center flex-shrink-0"
            style="width: 20px; min-width: 20px"
          >
            <div
              class="d-flex align-center justify-center"
              style="height: 24px"
            >
              <v-icon
                v-if="r.isFlagged"
                class="pa-0"
                x-small
                color="yellow"
              >
                mdi-circle
              </v-icon>
            </div>
            <div
              class="d-flex align-center justify-center"
              style="height: 28px"
            >
              <v-icon
                v-if="r.isStarred"
                class="ma-0"
                small
                color="primary"
                style="height: 16px!important; width: 16px!important;"
              >
                mdi-star
              </v-icon>
              <v-btn
                v-else-if="unreadReservations && r.isUnread"
                icon
                x-small
                class="ma-0 pa-0"
                @click.stop="markAsRead"
                @mousedown.stop
                @touchstart.native.stop
              >
                <v-icon
                  x-small
                  color="brand"
                >
                  mdi-circle
                </v-icon>
              </v-btn>
            </div>
          </div>

          <div
            class="d-flex flex-column flex-grow-1"
            style="min-width: 0"
          >
            <div
              class="d-flex align-center"
              style="min-height: 24px"
            >
              <div
                class="d-flex align-top flex-shrink-1"
                :style="`min-width: ${searchResult ? 140 : 50}px`"
              >
                <span
                  v-if="searchResult"
                  class="font-weight-bold pr-5 d-block-inline"
                  style="line-height: 24px; min-width: 50px; font-size: 18px; margin-top: -1px;"
                >{{ $localized.hourMinText(r.dateBegin) }} <span class="text--secondary px-2 opacity-50">|</span> {{ r.partySizeText }}</span>
                <span
                  v-if="!r.isBlock && !searchResult"
                  class="font-weight-bold d-block-inline"
                  style="line-height: 20px; min-width: 50px; font-size: 20px; margin-top: -1px;"
                >{{ r.partySizeText }}</span>
                <span
                  v-else-if="r.isBlock"
                  class="font-weight-medium ml-5"
                  style="min-width: 250px"
                >{{ $t('label.table_blocked') }}</span>
              </div>
              <div
                :class="[
                  'flex-grow-1 text-right text-body-2 ellipsize d-flex flex-row justify-end align-self-start',
                  {'error--text': r.tabItems.length === 0}]"
                style="line-height: 20px; min-width: 0"
              >
                {{ r.tabText }}
                <template v-if="r.tabItems.length > 0">
                  <!-- this is safe. String is defined by user, not displayed to other users. plus it's short -->
                  <!-- eslint-disable vue/no-v-html -->
                  -
                  <span
                    class="font-weight-bold ml-1 d-block-inline"
                    v-html="tablesHtmlText"
                  />
                  <!-- eslint-enable -->
                </template>
              </div>
            </div>

            <div
              v-if="!r.isBlock"
              style="min-width: 0"
            >
              <div
                class="d-flex align-center"
                style="min-height: 28px"
              >
                <div
                  class="d-flex align-center flex-grow-1 ellipsize"
                  style="gap: 2px; height: 28px; min-width: 0"
                >
                  <v-icon
                    v-if="r.contact.isVIP"
                    class="ma-0"
                    small
                    color="orange"
                  >
                    mdi-crown
                  </v-icon>
                  <span
                    v-if="r.nameText"
                    class="font-weight-medium ellipsize"
                    style="font-size: 1rem; line-height: 28px"
                  >{{ r.nameText }}</span>
                  <span
                    v-else
                    class="font-weight-medium text--secondary"
                    style="font-size: 1rem; line-height: 28px"
                  >{{ isWalkin ? $t('code.booking_type.walkin') : $t('placeholder.no_name') }}</span>
                  <sup
                    v-if="r.contact.reservationCount !== 0"
                    class="pl-1"
                  >{{ r.contact.reservationCount }}</sup>
                </div>
                <div
                  class="d-flex align-center justify-end flex-shrink-0"
                  style="width: 125px"
                >
                  <v-btn
                    v-if="searchResult"
                    depressed
                    :outlined="statusButtonOutlined(r.status)"
                    class="my-0"
                    style="min-width:125px"
                    small
                    :color="colorStatusButton"
                  >
                    {{ $t(`code.status.${r.statusText}`) }}
                  </v-btn>
                  <status-selection-menu
                    v-else
                    :value="r.status"
                    :reservation="r"
                    @item-clicked="statusChanged"
                  >
                    <template #activator="{ on: { click } }">
                      <hideable-badge
                        color="purple darken-3"
                        :content="waitlistBadgeContent"
                        :visible="waitlistBadgeVisible"
                      >
                        <v-btn
                          depressed
                          :outlined="statusButtonOutlined(r.status)"
                          class="my-0"
                          style="min-width:125px"
                          small
                          :color="colorStatusButton"
                          @click.stop="click"
                          @mousedown.stop
                          @touchstart.native.stop
                        >
                          {{ $t(`code.status.${r.statusText}`) }}
                        </v-btn>
                      </hideable-badge>
                    </template>
                  </status-selection-menu>
                </div>
              </div>
              <span
                v-if="r.contact.companyName"
                class="d-block text-body-2 text--secondary ellipsize"
                style="line-height: 1.5; margin-top: -8px"
              >{{ r.contact.companyName }}</span>
            </div>

            <div
              v-if="!r.isBlock"
              class="text-caption mt-05 mb-05"
              style="max-height: 40px"
            >
              <reservation-chips
                compact
                :reservation="r"
              />
            </div>

            <div
              v-if="!r.isBlock && r.notesText"
              class="text-caption ellipsize"
              style="max-height: 40px"
            >
              {{ r.notesText }}
            </div>
          </div>
        </div>

        <!--  -->
        <!-- Full View -->
        <!--  -->
        <v-row
          v-else
          class="glr-full-row pa-0 ma-0 d-flex align-center"
          style="max-width: 100%"
        >
          <!-- PARTYSIZE -->
          <v-col class="glr-partysize-col pa-0 ma-0">
            <span :class="`${r.partySize > 99 ? 'text-h7' : 'text-h6'} font-weight-bold`">{{ r.partySizeText }}</span>
          </v-col>
          <!-- NAME -->
          <v-col
            class="glr-name-col pa-0 flex-grow-1 flex-shrink-1 mr-3"
            align-self="center"
          >
            <div class="glr-name-line">
              <v-icon
                v-if="r.isFlagged"
                x-small
                style="width: 16px"
                color="yellow"
              >
                mdi-circle
              </v-icon>
              <v-btn
                v-if="unreadReservations && r.isUnread"
                icon
                x-small
                class="glr-unread-btn"
                @click.stop="markAsRead"
                @mousedown.stop
                @touchstart.native.stop
              >
                <v-icon
                  x-small
                  color="brand"
                >
                  mdi-circle
                </v-icon>
              </v-btn>
              <v-icon
                v-if="r.isStarred"
                small
                :color="$vuetify.theme.dark ? 'blue lighten-1' : 'primary'"
              >
                mdi-star
              </v-icon>
              <v-icon
                v-if="r.contact.isVIP"
                color="orange"
                small
              >
                mdi-crown
              </v-icon>
              <span
                :class="[
                  'glr-name-text flex-grow-1 flex-shrink-1',
                  `${(r.isFlagged || (unreadReservations && r.isUnread) || r.isStarred) ? 'ml-05' : 'ml-1'}`
                ]"
              >
                <span
                  v-if="r.nameText"
                  class="font-weight-medium text-h7"
                >{{ r.nameText }}<sup
                  v-if="r.contact.reservationCount !== 0"
                  class="glr-visit-count"
                >{{ r.contact.reservationCount }}</sup></span>
                <span
                  v-else
                  class="text-h7 text--secondary"
                >{{ isWalkin ? $t('code.booking_type.walkin') : $t('placeholder.no_name') }}<sup
                  v-if="r.contact.reservationCount !== 0"
                  class="glr-visit-count"
                >{{ r.contact.reservationCount }}</sup></span>
              </span>
            </div>
            <div
              v-if="r.contact.companyName"
              class="py-0 text-body-2 text--secondary ml-2"
            >
              {{ r.contact.companyName }}
            </div>
          </v-col>
          <!-- NOTE -->
          <v-col
            class="pa-0 text-body-2 text-left flex-grow-1 flex-shrink-1"
            style="max-height: 40px; overflow: hidden;"
          >
            <div class="text-left">
              <reservation-chips :reservation="r" />
            </div>
            <v-tooltip
              bottom
              open-delay="400"
              max-width="80%"
            >
              <template #activator="{on, attrs}">
                <div
                  class="text-left ellipsize"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ r.notesText }}
                </div>
              </template>
              <span class="text-pre-wrap">{{ r.notesText }}</span>
            </v-tooltip>
          </v-col>

          <v-col
            :class="[
              'pa-0 text-body-2 text-center ml-3 mr-3 glr-name',
              {'glr-name-compact': $vuetify.breakpoint.lgAndDown}]"
          >
            <div class="text-center font-weight-bold ellipsize">
              <!-- this is safe. String is defined by user, not displayed to other users. plus it's short -->
              <!-- eslint-disable-next-line vue/no-v-html -->
              <span v-html="tablesHtmlText" />
            </div>
            <div :class="[{'error--text': r.tabItems.length === 0}, 'text-center', 'ellipsize']">
              {{ r.tabText }}
            </div>
          </v-col>
          <!-- STATUS BUTTON -->
          <v-col class="glr-status-btn pr-3 pa-0 ma-0 align-center">
            <status-selection-menu
              :value="r.status"
              :reservation="r"
              @item-clicked="statusChanged"
            >
              <template #activator="{ on: { click } }">
                <hideable-badge
                  color="purple darken-4"
                  :content="waitlistBadgeContent"
                  :visible="waitlistBadgeVisible"
                >
                  <v-btn
                    width="150"
                    depressed
                    small
                    :outlined="statusButtonOutlined(r.status)"
                    :color="colorStatusButton"
                    @click.stop="click"
                    @mousedown.stop
                    @touchstart.native.stop
                  >
                    {{ $t(`code.status.${r.statusText}`) }}
                  </v-btn>
                </hideable-badge>
              </template>
            </status-selection-menu>
          </v-col>
        </v-row>
      </v-lazy>
    </v-list-item-content>
  </v-list-item>
</template>

<script lang='ts'>
import Vue, { PropType } from 'vue';
import StatusSelectionMenu from '@/components/views/guests/StatusSelectionMenu.vue';
import Reservation, { ReservationStatus, ReservationType } from '@/model/Reservation';
import ReservationChips from '@/components/views/guests/ReservationChips.vue';
import { mapActions } from 'vuex';
import { performSaveAction } from '@/services/vue-utils';
import { cloneModel, toMapById } from '@/model/model-utils';
import HideableBadge from '@/components/util/HideableBadge.vue';
import CancelReservationDialog from '@/components/dialogs/CancelReservationDialog.vue';

export default Vue.extend({
  name: 'GuestListRow',
  components: {
    StatusSelectionMenu, ReservationChips, HideableBadge, CancelReservationDialog,
  },
  props: {
    reservation: { type: Object as PropType<Reservation>, required: true },
    waitlistListOrder: { type: Number as PropType<number>, required: false, default: 0 },
    overbookedTabItems: { type: Set as PropType<Set<number>|undefined>, required: false, default: undefined },
    compact: { type: Boolean, required: false, default: false },
    searchResult: { type: Boolean, required: false, default: false },
    unreadReservations: { type: Boolean, required: false, default: false },
  },
  data() {
    return {
      confirmCancelReservation: false,
    };
  },
  computed: {
    r(): Reservation {
      return this.reservation;
    },
    isWalkin(): boolean {
      return this.r.bookingType === ReservationType.Walkin;
    },
    waitlistBadgeContent(): string | null {
      return this.waitlistListOrder.toString();
    },
    waitlistBadgeVisible(): boolean {
      return this.r.status === ReservationStatus.WaitingList;
    },
    colorStatusButton(): string {
      switch (this.r.status) {
        case ReservationStatus.Planned: return 'grey lighten-1';
        case ReservationStatus.Confirmed: return 'info white--text';
        case ReservationStatus.CheckedIn: return 'orange white--text';
        case ReservationStatus.CheckedOut: return 'grey-50 lighten-2 white--text';
        case ReservationStatus.Seated: return 'green white--text';
        case ReservationStatus.NonConfirmed: return 'purple white--text';
        case ReservationStatus.Cancelled: return 'red white--text';
        case ReservationStatus.Spam: return 'red white--text';
        case ReservationStatus.Deleted: return 'red white--text';
        case ReservationStatus.NoShow: return 'red white--text';
        case ReservationStatus.WaitingList: return 'white--text purple darken-4';
        default: return 'grey lighten-1';
      }
    },
    tablesHtmlText(): string {
      return this.r.tabItems.map((ti) => (this.overbookedTabItems?.has(ti.id)
        ? `<span class="error--text">${ti.no}</span>`
        : `${ti.no}`)).join(', ');
    },
  },
  methods: {
    ...mapActions(['sendReservation', 'setReservationFromContextStatusChange']),
    statusButtonOutlined(s: ReservationStatus) {
      return s === ReservationStatus.Planned;
    },
    async statusChanged(status: ReservationStatus) {
      if (status === ReservationStatus.Cancelled) {
        this.confirmCancelReservation = true;
        return;
      }

      const r = cloneModel(this.r);
      r.setStatus(status);
      const ok = await performSaveAction(
        undefined,
        async () => this.sendReservation({ reservation: r }),
      );
      if (ok) this.setReservationFromContextStatusChange(r);
    },
    async cancelReservation(requestRefund?: boolean) {
      const r = cloneModel(this.reservation);
      r.setStatus(ReservationStatus.Cancelled);
      r.isRefundRequested = requestRefund ?? null;
      const ok = await performSaveAction(
        undefined,
        async () => this.sendReservation({ reservation: r }),
      );
      if (ok) this.setReservationFromContextStatusChange(r);
    },
    async markAsRead() {
      const r = cloneModel(this.r);
      r.isUnread = false;
      await performSaveAction(
        undefined,
        async () => this.sendReservation({ reservation: r }),
      );
    },
  },
});
</script>

<style lang="scss" scoped>

  .glr-name {
    min-width: 200px;
    max-width: 200px;
    &.glr-name-compact {
      min-width: 100px;
      max-width: 100px
    }
  }

  .glr-status-btn {
    min-width: 160px;
    max-width: 160px;
  }

  .glr-note {
    max-height: 40px;
    overflow: hidden;
  }

  .glr-full-row {
    padding-left: 4px;
  }

  .glr-partysize-col {
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: stretch;
    box-sizing: border-box;
    width: 44px;
    min-width: 44px;
    max-width: 44px;
    flex: 0 0 44px;
    padding: 0 4px;
    border-right: 1px solid $grey-8-color;

    .theme--dark & {
      border-right-color: $grey-75-color;
    }
  }

  .glr-name-col {
    max-width: 220px;
    padding-left: 4px;
  }

  .glr-name-text {
    min-width: 0;
  }

  .glr-visit-count {
    margin-left: 0.35em;
    white-space: nowrap;
  }

  .glr-name-line {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 2px;
    max-width: 100%;
    min-width: 0;
    padding-left: 4px;

    .v-icon,
    .v-btn {
      flex-shrink: 0;
    }

    .v-icon {
      margin: 0;
    }
  }

  .glr-unread-btn {
    width: 16px !important;
    height: 16px !important;
    margin: 0 !important;
  }

  .mh-48 {
    min-height: 48px;
  }

</style>
